import styled from '@emotion/styled';
import { ReactNode, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { mqMin } from 'utils/responsive';

interface ModalOptions {
    /** ESCキーで閉じる? */
    keyboard?: boolean;
    /** 背景をクリックして閉じる? */
    maskClosable?: boolean;
    /** 領域外・またはesc押下でcloseした際のcallbackイベント */
    onClose?: () => void;
    /** コンテンツがはみ出す場合にスクロールさせるか（true）、はみ出し許容するか（false） */
    scrollable?: boolean;
}

/** モーダル背景 */
const ModalBg = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    @supports (height: 100svh) {
        width: 100svw;
        height: 100svh;
    }
    background-color: rgba(0, 0, 0, 0.32);
    opacity: ${(props: { isOpen: boolean }) => (props.isOpen ? 1 : 0)};
    z-index: var(--modal);
    pointer-events: ${(props: { isOpen: boolean }) => (props.isOpen ? 'auto' : 'none')};
    transition: opacity 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
`;

/** モーダル内容 */
const ModalContent = styled.div<{ scrollable: boolean }>`
    position: fixed;
    top: 15%;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    max-height: 70vh;
    max-width: 90vw;
    width: 90vw;
    border-radius: 8px;
    background-color: var(--white);
    box-shadow:
        0 11px 15px -7px #0003,
        0 24px 38px 3px #00000024,
        0 9px 46px 8px #0000001f;
    z-index: calc(var(--modal) + 1);
    width: 100%;

    ${mqMin('NARROW')} {
        width: inherit;
        min-width: 400px;
        max-width: 800px;
    }
`;

const ModalHeader = styled.div`
    position: sticky;
    top: 0;
    background-color: var(--white);
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: #666;
    font-size: 1.5rem;
    line-height: 1;
    transition: color 0.2s;

    &:hover {
        color: #333;
    }
`;

const ModalBody = styled.div<{ scrollable: boolean }>`
    flex-grow: 1;
    min-height: 0;
    padding: 10px 20px 20px 20px;
    margin-bottom: 20px;
    overflow-y: ${(props) => (props.scrollable ? 'auto' : 'visible')};
`;

export const useModal = () => {
    const [isOpened, setIsOpened] = useState(false);

    // モーダルが開いている間は背景のスクロールを無効にする
    useEffect(() => {
        if (isOpened) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // クリーンアップ関数
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpened]);

    // Portalの外側にイベント伝播させない
    const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

    const createModal = useCallback(
        (
            content: ReactNode,
            { keyboard = true, maskClosable = true, onClose = () => {}, scrollable = true }: ModalOptions = {},
        ) => {
            const closeOnEsc = (e: KeyboardEvent) => {
                if (isOpened && e.key === 'Escape') close();
            };

            if (keyboard) {
                document.addEventListener('keydown', closeOnEsc);
            }

            const close = () => {
                setIsOpened(false);
                if (keyboard) document.removeEventListener('keydown', closeOnEsc);
                onClose();
            };

            return createPortal(
                <div role="dialog" aria-modal="true" onClick={stopPropagation}>
                    <ModalBg isOpen={isOpened} onClick={maskClosable ? close : () => {}} />
                    {isOpened && (
                        <ModalContent scrollable={scrollable}>
                            <ModalHeader>
                                <CloseButton onClick={close}>×</CloseButton>
                            </ModalHeader>
                            <ModalBody scrollable={scrollable}>{content}</ModalBody>
                        </ModalContent>
                    )}
                </div>,
                document.getElementById('root') as HTMLElement,
            );
        },
        [isOpened],
    );

    return {
        openModal: useCallback(() => setIsOpened(true), []),
        closeModal: useCallback(() => setIsOpened(false), []),
        createModal,
    };
};
